import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../../../shared/context/AuthContext";
import {
  fetchRootTemplates,
  fetchUserTemplates,
} from "../api/templateApi";

const TemplatesCatalogContext = createContext(null);

export const useTemplatesCatalogContext = () => {
  const context = useContext(TemplatesCatalogContext);
  if (!context) {
    throw new Error(
      "useTemplatesCatalogContext must be used within a TemplatesCatalogProvider"
    );
  }
  return context;
};

/**
 * Session-scoped template catalog. Does not fetch on login.
 * Call ensureLoaded() on first real need; reloadTemplates() after create/delete.
 */
export const TemplatesCatalogProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [rootTemplates, setRootTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState("");
  const [errorsBySource, setErrorsBySource] = useState({
    root: "",
    user: "",
  });
  const [hasLoaded, setHasLoaded] = useState(false);

  const hasLoadedRef = useRef(false);
  const inFlightRef = useRef(null);

  const resetCatalog = useCallback(() => {
    hasLoadedRef.current = false;
    inFlightRef.current = null;
    setHasLoaded(false);
    setRootTemplates([]);
    setUserTemplates([]);
    setIsLoadingTemplates(false);
    setTemplateLoadError("");
    setErrorsBySource({ root: "", user: "" });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      resetCatalog();
    }
  }, [isAuthenticated, resetCatalog]);

  const reloadTemplates = useCallback(async () => {
    if (!isAuthenticated) return [];

    setIsLoadingTemplates(true);
    setTemplateLoadError("");
    setErrorsBySource({ root: "", user: "" });

    const [rootResult, userResult] = await Promise.allSettled([
      fetchRootTemplates(),
      fetchUserTemplates(),
    ]);

    const nextRoot =
      rootResult.status === "fulfilled" ? rootResult.value : [];
    const nextUser =
      userResult.status === "fulfilled" ? userResult.value : [];

    setRootTemplates(nextRoot);
    setUserTemplates(nextUser);

    const nextErrors = {
      root:
        rootResult.status === "rejected"
          ? "Global templates could not be loaded."
          : "",
      user:
        userResult.status === "rejected"
          ? "Your templates could not be loaded."
          : "",
    };
    setErrorsBySource(nextErrors);

    const hasRootError = !!nextErrors.root;
    const hasUserError = !!nextErrors.user;
    if (hasRootError && hasUserError) {
      setTemplateLoadError("Templates could not be loaded.");
    } else if (hasRootError || hasUserError) {
      setTemplateLoadError("Some templates could not be loaded.");
    }

    hasLoadedRef.current = true;
    setHasLoaded(true);
    setIsLoadingTemplates(false);
    return [...nextUser, ...nextRoot];
  }, [isAuthenticated]);

  const ensureLoaded = useCallback(async () => {
    if (!isAuthenticated) return [];
    if (hasLoadedRef.current) {
      return [...userTemplates, ...rootTemplates];
    }
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const request = reloadTemplates().finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = request;
    return request;
  }, [isAuthenticated, reloadTemplates, rootTemplates, userTemplates]);

  const templates = useMemo(
    () => [...userTemplates, ...rootTemplates],
    [userTemplates, rootTemplates]
  );

  const value = useMemo(
    () => ({
      templates,
      rootTemplates,
      userTemplates,
      isLoadingTemplates,
      templateLoadError,
      errorsBySource,
      hasLoaded,
      ensureLoaded,
      reloadTemplates,
    }),
    [
      templates,
      rootTemplates,
      userTemplates,
      isLoadingTemplates,
      templateLoadError,
      errorsBySource,
      hasLoaded,
      ensureLoaded,
      reloadTemplates,
    ]
  );

  return (
    <TemplatesCatalogContext.Provider value={value}>
      {children}
    </TemplatesCatalogContext.Provider>
  );
};
